import React from 'react';
import { Box, CircularProgress, IconButton, Paper, Popper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import SelectInput from '../../SelectInput';
import SelectDivision from '../../glossary/SelectDivision';
import { LineItemUnitEnum } from '../../glossary/glossary.type';
import { createLineItem } from '../../lineItem/lineitem.api';
import { LineItemDTO } from '../project.type';

const unitsList = Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit }));

const normalizeDescription = (value: string) => value.toLowerCase().replace(/\s+/g, '');

const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <strong>{text.slice(index, index + query.length)}</strong>
      {text.slice(index + query.length)}
    </>
  );
};

interface DescriptionGlossaryFieldProps {
  descriptionInput: string;
  onDescriptionInputChange: (value: string) => void;
  options: LineItemDTO[];
  isSearching: boolean;
  // Committed selection; null = nothing picked yet.
  selectedOption: LineItemDTO | null;
  onSelectExisting: (option: LineItemDTO) => void;
  // Called with the backend-created LineItem after "Save".
  onSaveNew: (created: LineItemDTO) => void;
  descriptionError?: string | false;
  // Called on Escape or blur outside the field without a pick; caller discards the edit.
  onDismiss?: () => void;
}

// TextField + Popper instead of MUI Autocomplete, which fought the nested "Add new" form over open/close state.
const DescriptionGlossaryField: React.FC<DescriptionGlossaryFieldProps> = ({
  descriptionInput,
  onDescriptionInputChange,
  options,
  isSearching,
  selectedOption,
  onSelectExisting,
  onSaveNew,
  descriptionError,
  onDismiss,
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);
  // Popper renders in a portal, so blur checks need its own ref in addition to anchorRef.
  const popperRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [addingNew, setAddingNew] = React.useState(false);
  const [newDivisionId, setNewDivisionId] = React.useState<string>('');
  const [newUnit, setNewUnit] = React.useState<string>(unitsList[0].value);
  const [isSavingNew, setIsSavingNew] = React.useState(false);

  const normalizedInput = normalizeDescription(descriptionInput);
  const exactMatch = selectedOption ? null : (options.find(option => option.isExactMatch) ?? null);
  const showAddNewRow = !selectedOption && !exactMatch && !isSearching && normalizedInput.length > 0;

  const closeDropdown = () => {
    setOpen(false);
    setAddingNew(false);
  };

  const handleFocus = () => {
    if (!addingNew) setOpen(true);
  };

  const handleInputChange = (value: string) => {
    onDescriptionInputChange(value);
    setAddingNew(false);
    setOpen(true);
  };

  const handleSelectExisting = (option: LineItemDTO) => {
    onSelectExisting(option);
    closeDropdown();
  };

  const openAddNewForm = () => {
    setAddingNew(true);
    setNewDivisionId('');
    setNewUnit(unitsList[0].value);
  };

  const backToOptions = () => setAddingNew(false);

  const handleSaveNew = async () => {
    setIsSavingNew(true);
    try {
      const created = await createLineItem({
        description: descriptionInput,
        unit: newUnit,
        divisionId: newDivisionId || null,
      });
      onSaveNew(created);
      closeDropdown();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add the new glossary entry.');
    } finally {
      setIsSavingNew(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box ref={anchorRef}>
        <TextField
          inputRef={inputRef}
          label='Description'
          fullWidth
          variant='standard'
          value={descriptionInput}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onClick={handleFocus}
          onKeyDown={e => {
            if (e.key === 'Escape' && onDismiss) {
              e.stopPropagation();
              closeDropdown();
              onDismiss();
            }
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (isSavingNew) return;
              const active = document.activeElement;
              const stillInside = active && (anchorRef.current?.contains(active) || popperRef.current?.contains(active));
              if (!stillInside) {
                closeDropdown();
                if (onDismiss) onDismiss();
                else if (!selectedOption) onDescriptionInputChange('');
              }
            }, 150);
          }}
          error={Boolean(descriptionError)}
          helperText={descriptionError || undefined}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: isSearching ? <CircularProgress color='inherit' size={20} /> : null,
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{ width: Math.max(anchorRef.current?.clientWidth ?? 0, 320), zIndex: 1301 }}
      >
        <Paper ref={popperRef} sx={{ width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,.14)', overflow: 'hidden' }}>
          {addingNew ? (
            <Box sx={{ padding: '12px' }}>
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                Add new glossary entry
              </Typography>
              <Box sx={{ fontSize: 14, fontWeight: 600, mb: 1.5 }}>“{descriptionInput}”</Box>
              <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1.5 }}>
                <Box sx={{ width: 160, flexShrink: 0 }}>
                  <SelectDivision selectedDivision={newDivisionId} setSelectedDivision={value => setNewDivisionId(value ?? '')} />
                </Box>
                <Box sx={{ width: 100, flexShrink: 0 }}>
                  <SelectInput label='Unit' selected={newUnit} setSelected={setNewUnit} list={unitsList} />
                </Box>
              </Stack>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                <Tooltip title='Back'>
                  <Box component='span' sx={{ display: 'inline-flex' }}>
                    <IconButton size='small' color='error' onClick={backToOptions} disabled={isSavingNew}>
                      <ArrowBack fontSize='small' />
                    </IconButton>
                  </Box>
                </Tooltip>
                <Tooltip title='Save'>
                  <Box component='span' sx={{ display: 'inline-flex' }}>
                    <IconButton size='small' color='primary' onClick={handleSaveNew} disabled={isSavingNew}>
                      {isSavingNew ? <CircularProgress size={16} /> : <Save fontSize='small' />}
                    </IconButton>
                  </Box>
                </Tooltip>
              </Stack>
            </Box>
          ) : (
            <>
              <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {options.length === 0 && !isSearching && normalizedInput.length > 0 && (
                  <Box sx={{ padding: '10px 12px', fontSize: 12, color: 'text.secondary' }}>No matches in the glossary.</Box>
                )}
                {options.length === 0 && !isSearching && normalizedInput.length === 0 && (
                  <Box sx={{ padding: '10px 12px', fontSize: 12, color: 'text.secondary' }}>Start typing to search the glossary.</Box>
                )}
                {options.map(option => {
                  const isSelected = option.id === selectedOption?.id;
                  return (
                    <Box
                      key={option.id}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleSelectExisting(option)}
                      sx={{
                        fontSize: 13,
                        padding: '6px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'rgba(2, 131, 160, 0.1)' : undefined,
                        fontWeight: isSelected ? 700 : undefined,
                        '&:hover': { backgroundColor: isSelected ? 'rgba(2, 131, 160, 0.16)' : 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <HighlightedText text={option.description} query={descriptionInput} />
                      </Box>
                      {option.divisionCode && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#0283a0',
                            background: '#e7f1f3',
                            border: '1px solid #bcd9de',
                            borderRadius: 11,
                            padding: '1px 8px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>Div {option.divisionCode}</span>
                          {option.divisionName && <span style={{ opacity: 0.7, fontWeight: 400 }}>{option.divisionName}</span>}
                        </Box>
                      )}
                      {option.unit && (
                        <Box sx={{ fontSize: 11, color: 'text.secondary', width: 26, textAlign: 'right', flexShrink: 0 }}>
                          {option.unit}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                {exactMatch && (
                  <>
                    <Box sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }} />
                    <Box sx={{ padding: '10px 12px', minWidth: 0 }}>
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', overflowWrap: 'break-word' }}>
                        “{exactMatch.description}” already exists — pick it above. A duplicate can’t be created.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
              {showAddNewRow && (
                <Box
                  onMouseDown={e => e.preventDefault()}
                  onClick={openAddNewForm}
                  sx={{
                    padding: '10px 12px',
                    fontSize: 13,
                    fontWeight: 800,
                    color: '#0283a0',
                    cursor: 'pointer',
                    background: '#fafafa',
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: '0 -4px 6px -4px rgba(0,0,0,.12)',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
                  }}
                >
                  Add new: “{descriptionInput}”
                </Box>
              )}
            </>
          )}
        </Paper>
      </Popper>
    </Box>
  );
};

export default DescriptionGlossaryField;
